import React, { useState, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useLocation, useNavigate } from "react-router-dom";
import { getData, postData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import "../Styles/mtpstyle.css"; // Import custom styles
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap"; // Import Reactstrap modal
import CardHeaderSpan from "../../../Utils/CommonComponents/CardHeaderSpan";
import { toast } from "react-toastify"; // Import toast for notifications
import { dynamicImage } from "../../../Utils";
import { Image } from "../../../AbstractElements";
import { isOwnRequest } from "../../../Utils/authUtils";

const formatIsoDate = (d?: any): string => {
  if (!d) return "";
  const str = String(d).trim();
  if (str.includes("T")) return str.split("T")[0];
  if (str.includes(" ")) return str.split(" ")[0];
  return str;
};

const MTPBigCalendar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mtpStatus: initialMtpStatus, updated, mtpId: stateMtpId, backPath, backState, itemData } = location.state || {};

  const [currentMtpStatus, setCurrentMtpStatus] = useState<string>(initialMtpStatus || "DRAFT");
  const [events, setEvents] = useState<any[]>([]); // State for calendar events
  const [days, setDays] = useState<any[]>([]); // State for days data
  const [labels, setLabels] = useState<string>(""); // State for dynamic labels
  const [modalOpen, setModalOpen] = useState(false); // State for approve/reject modal visibility
  const [actionType, setActionType] = useState<"approve" | "reject">(); // State for action type
  const [item, setItem] = useState<any>(null); // State for the selected MTP item
  const [loader, setLoader] = useState(false); // State for loader
  const [loading, setLoading] = useState(true); // State for API loading
  const serverUrl = localStorage.getItem("serverUrl") || "";

  // Parse and format month for FullCalendar initialDate
  const rawMonth = updated || itemData?.Month;
  let initialDate: string | undefined = undefined;
  if (rawMonth) {
    if (rawMonth.includes("-")) {
      const parts = rawMonth.split("-");
      if (parts[0].length === 4) {
        // YYYY-MM -> YYYY-MM-01
        initialDate = `${parts[0]}-${parts[1]}-01`;
      } else {
        // MM-YYYY -> YYYY-MM-01
        initialDate = `${parts[1]}-${parts[0]}-01`;
      }
    }
  }

  // Fetch data for the calendar
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Show loader
      try {
        let currentMtpId = stateMtpId || itemData?.MtpId || itemData?.mtp_id || itemData?.id;

        // If mtpId is not provided directly, try looking it up by month
        if (!currentMtpId && rawMonth) {
          const monthQuery = rawMonth.includes("-") && rawMonth.split("-")[0].length !== 4
            ? rawMonth
            : rawMonth.split("-").reverse().join("-");

          let lookupUrl = `${serverUrl}${API.getTeamMtpStatus}`;
          let lookupRes = await getData(lookupUrl, true, { month: monthQuery });
          if (lookupRes?.status === 200 && Array.isArray(lookupRes?.data?.data)) {
            const found = lookupRes.data.data[0];
            if (found) {
              currentMtpId = found.MtpId || found.mtp_id || found.id;
            }
          }
        }

        if (!currentMtpId) {
          setLoading(false);
          return;
        }

        const url = `${serverUrl}${API.getMtpByIdV2}`;
        const { status, data } = await getData(url, true, {
          mtp_id: currentMtpId,
          last_log_id: 0,
        });

        if (status === 200 && data?.data) {
          const fetchedStatus = data?.data?.mtp?.MtpStatus || data?.data?.mtp?.mtp_status || data?.data?.mtp?.status || data?.data?.MtpStatus || data?.data?.status;
          if (fetchedStatus) {
            setCurrentMtpStatus(fetchedStatus);
          }
          const calendarEvents: any[] = [];

          // Add holidays
          if (Array.isArray(data.data.holiday)) {
            data.data.holiday.forEach(
              (holiday: any) => {
                const date = formatIsoDate(holiday.HolidayDate || holiday.holiday_date);
                const name = holiday.HolidayName || holiday.holiday_name;
                if (date) {
                  calendarEvents.push({
                    title: name || "Holiday",
                    date: date,
                    backgroundColor: "#30c9f7", // Blue for holidays
                    textColor: "#fff",
                    type: "Holiday",
                    details: holiday,
                  });
                }
              }
            );
          }

          // Add week-offs
          if (Array.isArray(data.data.weekOff)) {
            data.data.weekOff.forEach((weekOffDate: string) => {
              const formattedWeekOff = formatIsoDate(weekOffDate);
              if (formattedWeekOff) {
                calendarEvents.push({
                  title: "Week Off",
                  date: formattedWeekOff,
                  backgroundColor: "#9e059e", // Purple for week-offs
                  textColor: "#fff",
                  type: "Week Off",
                });
              }
            });
          }

          // Add leaves
          if (Array.isArray(data.data.leaves)) {
            data.data.leaves.forEach(
              (leave: any) => {
                const leaveStatusText =
                  leave.LeaveStatus === 1 || leave.leave_status === 1
                    ? "Raised"
                    : leave.LeaveStatus === 2 || leave.leave_status === 2
                      ? "Approved"
                      : leave.LeaveStatus === 3 || leave.leave_status === 3
                        ? "Rejected"
                        : leave.LeaveStatus === 4 || leave.leave_status === 4
                          ? "Canceled"
                          : "Unknown";

                const lDate = formatIsoDate(leave.LeaveDate || leave.leave_date);
                const lType = leave.LeaveType || leave.leave_type || "Leave";
                if (lDate) {
                  calendarEvents.push({
                    title: `${lType}:${leaveStatusText}`,
                    date: lDate,
                    backgroundColor: "orange", // Orange for leaves
                    textColor: "#fff",
                    type: "Leave",
                    details: leave,
                  });
                }
              }
            );
          }

          // Add agenda days with Coverage counts
          if (Array.isArray(data.data.days)) {
            data.data.days.forEach((day: any) => {
              const dayDate = formatIsoDate(day.MtpDayDate || day.mtp_day_date || day.Date || day.date || day.TpDate || day.tp_date);
              if (!dayDate) return;

              // Compute coverage string
              let coverageCounts = "";
              if (Array.isArray(day.Coverage) && day.Coverage.length > 0) {
                coverageCounts = day.Coverage.map((c: any) => (c.count !== undefined ? c.count : c.Count ?? 0)).join("/");
              } else if (day.outlet_count !== undefined && day.outlet_count !== null) {
                coverageCounts = String(day.outlet_count);
              } else if (day.Doctor !== undefined || day.Chemist !== undefined || day.Stockist !== undefined) {
                coverageCounts = `${day.Doctor || 0}/${day.Chemist || 0}/${day.Stockist || 0}`;
              } else if (day.coverage) {
                coverageCounts = String(day.coverage);
              }

              if (coverageCounts) {
                calendarEvents.push({
                  title: coverageCounts,
                  date: dayDate,
                  backgroundColor: (day.Underload || day.Upperload) ? "#808080" : "#11bd5c", // Green or Grey for Coverage
                  textColor: "#fff",
                  type: "Coverage",
                  details: day,
                });
              }

              // Second event: NCA (only if NCA is not zero or agenda is NCA)
              const ncaVal = day.NCA !== undefined ? day.NCA : day.nca || 0;
              const agendaVal = day.agenda || day.Agendacontroltype || day.agendacontrol_type || "";
              if (ncaVal !== 0 || (agendaVal && String(agendaVal).toUpperCase() === "NCA")) {
                calendarEvents.push({
                  title: `NCA${ncaVal ? `: ${ncaVal}` : ""}`,
                  date: dayDate,
                  backgroundColor: "red", // Red for NCA
                  textColor: "#fff",
                  type: "NCA",
                  details: day,
                });
              }
            });
          }

          setEvents(calendarEvents); // Update state with events
          setDays(data?.data?.days || []); // Store the days data

          // Set dynamic labels
          const dynamicLabels =
            data?.data?.mtp?.Labels ||
            data?.data?.mtp?.labels ||
            data?.data?.Labels ||
            data?.data?.labels ||
            (Array.isArray(data?.data?.outlets)
              ? data.data.outlets
                .map((o: any) => o.OutlettypeName || o.outlet_type_name || o.Name || o.name)
                .filter(Boolean)
                .join("/")
              : "") ||
            "";
          setLabels(dynamicLabels);
        }
      } catch (error) {
        console.error("Error fetching calendar data:", error);
      } finally {
        setLoading(false); // Hide loader
      }
    };

    fetchData();
  }, [stateMtpId, rawMonth, serverUrl]);

  const handleDateClick = (arg: any) => {
    const clickedDate = arg.dateStr;
    const dayObj = days.find((d: any) => formatIsoDate(d.MtpDayDate || d.mtp_day_date || d.date) === clickedDate);
    const targetMtpId = stateMtpId || itemData?.MtpId || itemData?.mtp_id || itemData?.id;

    navigate("/mtpdayplanner", {
      state: {
        date: clickedDate,
        dayId: dayObj?.MtpDayId || dayObj?.mtp_day_id || 0,
        mtpId: targetMtpId,
        mtpStatus: currentMtpStatus,
      },
    });
  };

  // Handle event click - navigate directly to planner
  const handleEventClick = (info: any) => {
    const clickedEvent = info.event.extendedProps;
    const mtpDayId = clickedEvent.details?.MtpDayId || clickedEvent.details?.mtp_day_id || clickedEvent.details?.id || 0;
    const eventDate = info.event.startStr || clickedEvent.details?.MtpDayDate || clickedEvent.details?.mtp_day_date;
    const targetMtpId = stateMtpId || itemData?.MtpId || itemData?.mtp_id || itemData?.id;

    navigate("/mtpdayplanner", {
      state: {
        date: eventDate,
        dayId: mtpDayId,
        mtpId: targetMtpId,
        mtpStatus: currentMtpStatus,
      },
    });
  };

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [resetting, setResetting] = useState<boolean>(false);
  const [confirmType, setConfirmType] = useState<"submit" | "reset" | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

  const submitMtp = async () => {
    const targetMtpId = stateMtpId || item?.MtpId || itemData?.MtpId || itemData?.mtp_id || itemData?.id;
    if (!targetMtpId) {
      toast.warning("MTP ID is missing.");
      return;
    }
    setSubmitting(true);
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";
      const url = `${serverBaseUrl}${API.submitMTP}`;
      const res = await getData(url, true, { mtp_id: targetMtpId });
      if (res?.status === 200 || (res as any)?.statusCode === 200) {
        toast.success((res as any)?.data?.message || (res as any)?.message || "MTP submitted successfully!");
        setCurrentMtpStatus("REQUESTED");
      } else {
        toast.error((res as any)?.message || (res as any)?.data?.message || "Failed to submit MTP.");
      }
    } catch (err) {
      console.error("Error submitting MTP:", err);
      toast.error("Failed to submit MTP.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetMtp = async () => {
    const targetMtpId = stateMtpId || item?.MtpId || itemData?.MtpId || itemData?.mtp_id || itemData?.id;
    if (!targetMtpId) {
      toast.warning("MTP ID is missing.");
      return;
    }
    setResetting(true);
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) serverBaseUrl += "/";
      const url = `${serverBaseUrl}${API.resetMTP}`;
      const res = await postData(url, true, { mtp_id: targetMtpId });
      if (res?.status === 200 || (res as any)?.statusCode === 200) {
        toast.success((res as any)?.data?.message || (res as any)?.message || "MTP reset successfully!");
        window.location.reload();
      } else {
        toast.error((res as any)?.message || (res as any)?.data?.message || "Failed to reset MTP.");
      }
    } catch (err) {
      console.error("Error resetting MTP:", err);
      toast.error("Failed to reset MTP.");
    } finally {
      setResetting(false);
    }
  };

  // Change MTP status (Approve/Reject)
  const changeMtpStatus = async (action: "approve" | "reject") => {
    setLoader(true);
    try {
      const url = `${serverUrl}${action === "approve" ? API.approveMTP : API.rejectMTP
        }`;
      const params = { mtp_id: item?.MtpId || item?.mtp_id || stateMtpId };

      const { status, data } = await getData(url, true, params);

      if (status === 200) {
        toast.success(data.message || "MTP Status Updated Successfully!");
      } else {
        toast.error(data.message || "Failed to change MTP status.");
      }
    } catch (error) {
      console.error("Error changing MTP status:", error);
    } finally {
      setLoader(false);
      setModalOpen(false);
    }
  };

  return (
    <div className="page-body">
      <CardHeaderSpan
        headingClassName="py-2"
        back={true}
        backPath={backPath ?? "/Approvals"}
        backState={backState ?? { activeTab: "1" }}
        heading={"MTP Calendar"}
        span={"ReportListTitle"}
      />

      {loading ? (
        <div className="text-center py-5">
          <Image
            className="b-r-10 img-60"
            src={dynamicImage("dashboard/loader.gif")}
            alt="Loading..."
          />
        </div>
      ) : (
        <>
          <div className="legend-container d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex flex-wrap gap-2">
              <span className="badge bg-info p-2">Holiday</span>
              <span className="badge bg-purple p-2" style={{ backgroundColor: "#9e059e" }}>Week Off</span>
              <span className="badge bg-warning p-2 text-dark">Leave</span>
              <span className="badge bg-danger p-2">NCA</span>
              <span className="badge bg-success p-2 rounded-pill px-3" style={{ backgroundColor: "#11bd5c" }}>
                {labels || "Planned Outlets"}
              </span>
            </div>

            {/* Approve/Reject Buttons */}
            {currentMtpStatus?.toLowerCase() === "requested" && !isOwnRequest(itemData) && (
              <div className="action-buttons">
                <Button
                  color="success"
                  className="me-2"
                  onClick={() => {
                    setActionType("approve");
                    setItem({ MtpId: stateMtpId });
                    setModalOpen(true);
                  }}
                >
                  Approve
                </Button>
                <Button
                  color="danger"
                  onClick={() => {
                    setActionType("reject");
                    setItem({ MtpId: stateMtpId });
                    setModalOpen(true);
                  }}
                >
                  Reject
                </Button>
              </div>
            )}
          </div>

          <div className="mtp-calendar-wrapper">
            <style>{`
              .fc .fc-daygrid-day-top {
                display: flex !important;
                flex-direction: row !important;
                justify-content: center !important;
                align-items: center !important;
                text-align: center !important;
                padding: 6px 0 4px 0 !important;
              }
              .fc .fc-daygrid-day-number {
                float: none !important;
                text-align: center !important;
                margin: 0 auto !important;
                padding: 0 !important;
                font-size: 13px !important;
                font-weight: 600 !important;
                color: #475569 !important;
                text-decoration: none !important;
                display: inline-block !important;
              }
              .fc .fc-day-other .fc-daygrid-day-number {
                color: #94a3b8 !important;
                opacity: 0.7 !important;
              }
              .fc .fc-daygrid-day-frame {
                min-height: 95px !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                justify-content: flex-start !important;
                padding: 0 4px 6px 4px !important;
              }
              .fc .fc-daygrid-day-events {
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                justify-content: flex-start !important;
                width: 100% !important;
                min-height: auto !important;
                margin: 0 !important;
                padding: 0 !important;
                gap: 3px !important;
              }
              .fc .fc-daygrid-event-harness {
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
                width: 100% !important;
                margin: 2px 0 !important;
                padding: 0 !important;
                position: static !important;
              }
              .fc .fc-event,
              .fc .fc-daygrid-event,
              .fc .fc-daygrid-block-event,
              .fc .fc-h-event {
                display: inline-flex !important;
                justify-content: center !important;
                align-items: center !important;
                text-align: center !important;
                width: auto !important;
                min-width: 82px !important;
                max-width: 95px !important;
                margin: 0 auto !important;
                padding: 3px 8px !important;
                border-radius: 4px !important;
                border: none !important;
                font-size: 11px !important;
                font-weight: 600 !important;
                line-height: 1.2 !important;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12) !important;
                cursor: pointer !important;
                transition: transform 0.15s ease, box-shadow 0.15s ease !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                white-space: nowrap !important;
              }
              .fc .fc-event:hover {
                transform: translateY(-1px) !important;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
              }
              .fc .fc-event-main {
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
                text-align: center !important;
                width: 100% !important;
                padding: 0 !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                white-space: nowrap !important;
              }
              .fc .fc-event-main-frame {
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
                text-align: center !important;
                width: 100% !important;
              }
              .fc .fc-event-title-container {
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
                text-align: center !important;
                width: 100% !important;
                flex-grow: 0 !important;
              }
              .fc .fc-event-title {
                text-align: center !important;
                font-size: 11px !important;
                font-weight: 600 !important;
                width: 100% !important;
                display: inline-block !important;
              }
            `}</style>
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              initialDate={initialDate} // Dynamically set initial date
              events={events} // Pass events to calendar
              eventDisplay="block"
              height="auto"
              headerToolbar={{
                left: "",
                center: "title",
                right: "",
              }}
              dateClick={handleDateClick}
              eventClick={handleEventClick} // Handle event clicks
            />
          </div>

          {/* Bottom Actions: Reset MTP & Submit MTP (Only for Draft or Rejected) */}
          {(() => {
            const statusLower = `${currentMtpStatus || ''}`.toLowerCase();
            const isDraftOrRejected = statusLower.includes("draft") || statusLower.includes("reject") || statusLower.includes("rejected");
            if (!isDraftOrRejected) return null;

            return (
              <div className="d-flex justify-content-end gap-3 mt-4 mb-3">
                <Button
                  color="warning"
                  className="px-4 font-weight-bold"
                  disabled={resetting}
                  onClick={() => {
                    setConfirmType("reset");
                    setConfirmModalOpen(true);
                  }}
                >
                  {resetting ? "Resetting..." : "Reset MTP"}
                </Button>
                <Button
                  color="success"
                  className="px-4 font-weight-bold"
                  disabled={submitting}
                  onClick={() => {
                    setConfirmType("submit");
                    setConfirmModalOpen(true);
                  }}
                >
                  {submitting ? "Submitting..." : "Submit MTP"}
                </Button>
              </div>
            );
          })()}
        </>
      )}

      {/* Modal for Submit / Reset Confirmation */}
      <Modal isOpen={confirmModalOpen} toggle={() => setConfirmModalOpen(false)}>
        <ModalHeader toggle={() => setConfirmModalOpen(false)}>
          {confirmType === "submit" ? "Confirm Submit MTP" : "Confirm Reset MTP"}
        </ModalHeader>
        <ModalBody>
          <p>
            {confirmType === "submit"
              ? "Are you sure you want to submit this MTP?"
              : "Are you sure you want to reset this MTP? All day plans for this month will be cleared."}
          </p>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              color={confirmType === "submit" ? "success" : "warning"}
              onClick={() => {
                setConfirmModalOpen(false);
                if (confirmType === "submit") {
                  submitMtp();
                } else if (confirmType === "reset") {
                  resetMtp();
                }
              }}
            >
              Yes
            </Button>
            <Button color="secondary" onClick={() => setConfirmModalOpen(false)}>
              No
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Modal for Approve/Reject */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          Confirm Action
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to {actionType} this MTP?</p>
          <div className="d-flex justify-content-end gap-2">
            <Button
              color="primary"
              onClick={() => changeMtpStatus(actionType!)}
            >
              Yes
            </Button>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              No
            </Button>
          </div>
        </ModalBody>
      </Modal>

    </div>
  );
};

export default MTPBigCalendar;
